import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import SEOHead from "@/components/feature/SEOHead";
import { getArticleById } from "@/mocks/articleData";

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(parseInt(id)) : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 md:pt-18 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-5">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">
              文章未找到
            </h1>
            <p className="text-sm text-foreground-600 mb-6">
              抱歉，您要查看的文章不存在或已被移除。
            </p>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回赛事资讯
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: "首页", path: "/" },
    { label: "赛事资讯", path: "/category" },
    { label: article.category, path: `/category?tab=${article.categorySlug}` },
    { label: article.title },
  ];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    datePublished: article.date,
    author: {
      "@type": "Organization",
      name: "开云体育",
    },
    publisher: {
      "@type": "Organization",
      name: "开云体育",
    },
    description: article.content.replace(/<[^>]*>/g, "").substring(0, 160),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.kaiyunsports.com/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbItems.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.label,
      item: item.path ? `https://www.kaiyunsports.com${item.path}` : undefined,
    })),
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 开云体育`}
        description={article.content.replace(/<[^>]*>/g, "").substring(0, 160)}
        keywords={`${article.category},开云体育,体育资讯,赛事分析`}
        canonicalUrl={`https://www.kaiyunsports.com/article/${article.id}`}
        ogType="article"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />

      <main className="pt-16 md:pt-18">
        <article className="w-full px-4 md:px-6 max-w-4xl mx-auto">
          <div className="py-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                {article.category}
              </span>
              <span className="text-xs text-foreground-500">{article.date}</span>
              <span className="text-xs text-foreground-500 flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views.toLocaleString()} 阅读
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6 leading-tight">
              {article.title}
            </h1>

            <div className="w-full h-64 md:h-80 rounded-xl overflow-hidden mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>

          {article.relatedArticles.length > 0 && (
            <section className="mt-12 mb-16 pt-8 border-t border-background-200">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {article.relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group rounded-xl bg-background-100 border border-background-200/60 overflow-hidden cursor-pointer hover:border-primary-300 transition-all duration-300"
                  >
                    <div className="w-full h-40 overflow-hidden">
                      <img
                        src={related.imageUrl}
                        alt={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {related.title}
                      </h3>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span>{related.date}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {related.views.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}